import React from 'react';
import { HandIcon, LightbulbIcon, TimerIcon } from 'lucide-react';
const stations = [{
  app: 'Floor',
  color: 'bg-coral',
  text: 'text-white',
  drill: 'Cartwheel along a line, hand-hand-foot-foot ×5',
  cue: '“Each hand and foot on the line, in order”',
  min: 8
}, {
  app: 'Bars',
  color: 'bg-indigo',
  text: 'text-white',
  drill: 'Back hip circle prep: front support, rock hips to the bar, spotted ×5',
  cue: '“Hips to the bar, look at your knees”',
  min: 8
}, {
  app: 'Beam',
  color: 'bg-teal',
  text: 'text-white',
  drill: 'Low-beam cartwheel prep: hands on beam, hips over, land on mat ×4',
  cue: '“Strong arms, hips over hands”',
  min: 8
}, {
  app: 'Vault',
  color: 'bg-amber',
  text: 'text-ink',
  drill: 'Run → hurdle → straddle shape in the air onto stack ×6',
  cue: '“Run, punch, open wide”',
  min: 8
}];
const ladder = [{
  dots: 1,
  level: 'Easier',
  body: 'Over a low mat mound, bent legs, hands on, hips over, land one foot then the other'
}, {
  dots: 2,
  level: 'On-target',
  body: 'Cartwheel along a line, hand-hand-foot-foot, legs apart, staying sideways'
}, {
  dots: 3,
  level: 'Harder',
  body: 'Straight-leg cartwheel, strong lunge to start and a lunge to finish, on a line'
}];
function Dots({
  n
}: {
  n: number;
}) {
  return <span className="flex gap-0.5" aria-label={`${n} of 3`} data-id="element-295" data-mp="3l5wod2ro0hq5">
      {[1, 2, 3].map(i => <span key={i} className={`h-2 w-2 rounded-full ${i <= n ? 'bg-coral' : 'bg-cloud-dark'}`} data-id="element-296" data-mp="3h54ce1reryq6" />)}
    </span>;
}
function PageFrame({
  tag,
  title,
  children
}: {
  tag: string;
  title: string;
  children: React.ReactNode;
}) {
  return <article className="overflow-hidden rounded-2xl bg-white text-ink shadow-book" data-id="element-297" data-mp="crxkleygbs23">
      <div className="flex items-center justify-between bg-coral px-5 py-2.5 text-white" data-id="element-298" data-mp="7wrvy8dnzzab">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em]" data-id="element-299" data-mp="3x11nuoxj111">{tag}</span>
        <span className="text-[10px] font-semibold uppercase tracking-widest text-white/80" data-id="element-300" data-mp="10gqfd83c7tv4">Vol. 3 · Connect &amp; Combine</span>
      </div>
      <div className="p-5 sm:p-6" data-id="element-301" data-mp="2pwavd1ejwxuo">
        <h3 className="font-display text-xl font-extrabold leading-tight tracking-tight sm:text-2xl" data-id="element-302" data-mp="2pxc95dmybyg3">{title}</h3>
        {children}
      </div>
    </article>;
}
export function SamplePlan() {
  return <section aria-labelledby="sample-heading" className="w-full bg-cloud" data-id="element-303" data-mp="2g5g7xtpkjs7n">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:py-20" data-id="element-304" data-mp="2l52lk49b7agz">
        <div className="text-center" data-id="element-305" data-mp="2l381es5v3mdy">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-coral-deep" data-id="element-306" data-mp="275jsmip2k5p9">Look inside</p>
          <h2 id="sample-heading" className="mt-3 font-display text-3xl font-extrabold text-ink sm:text-5xl" data-id="element-307" data-mp="2c5634kdij7ud">
            One real week, both pages
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-slate" data-id="element-308" data-mp="226qdymy1q8ni">
            Volume 3, Week 19: the cartwheel. Page A is the class you teach. Page B
            is the skill, at three levels, with the cue, the #1 error and the fix.
            Every one of the 52 weeks looks like this.
          </p>
        </div>

        <div className="mt-10 grid gap-5 lg:grid-cols-2" data-id="element-309" data-mp="1kwuvzjg8qjp9">
          <PageFrame tag="Page A · Rotation Map" title="Week 19 Class: Cartwheel Across the Floor" data-id="element-310" data-mp="1kvbexqmrg4mf">
            <dl className="mt-3 grid gap-1.5 text-[13px] text-slate" data-id="element-311" data-mp="1fw8943yyhude">
              <div data-id="element-312" data-mp="1unz5w4r27ed8"><dt className="inline font-semibold text-ink" data-id="element-313" data-mp="1uos7krb5vdoi">Objective: </dt><dd className="inline" data-id="element-314" data-mp="1pvz19z01nxab">a cartwheel with a clear hand-hand-foot-foot rhythm, along a line.</dd></div>
              <div data-id="element-315" data-mp="23muf7ppeh4kr"><dt className="inline font-semibold text-ink" data-id="element-316" data-mp="23mugdt14zamc">Group split: </dt><dd className="inline" data-id="element-317" data-mp="1zmag4gl9nqvz">four groups of 3–5. Floor is your teaching station.</dd></div>
              <div data-id="element-318" data-mp="2dm1uiifp39ai"><dt className="inline font-semibold text-ink" data-id="element-319" data-mp="2dltckc4ptdsg">Warm-up (8 min): </dt><dd className="inline" data-id="element-320" data-mp="2djz3bv2t3a47">shape calls, star holds, side-lunge reaches, wrist circles.</dd></div>
            </dl>
            <ul className="mt-4 grid gap-2" data-id="element-321" data-mp="j80wfzryyqbh">
              {stations.map(s => <li key={s.app} className="grid grid-cols-[64px_1fr_auto] items-start gap-3 rounded-xl border border-cloud-dark p-2.5" data-id="element-322" data-mp="j9vft9nleadg">
                  <span className={`rounded-lg px-2 py-1 text-center text-[10px] font-bold uppercase tracking-wider ${s.color} ${s.text}`} data-id="element-323" data-mp="fqwbmqv20y27">{s.app}</span>
                  <span className="text-[12.5px] leading-snug text-ink" data-id="element-324" data-mp="krak2a0kia0r">
                    {s.drill}
                    <span className="mt-0.5 block text-[12px] italic text-slate" data-id="element-325" data-mp="pm7vfnlbkvgt">{s.cue}</span>
                  </span>
                  <span className="flex items-center gap-1 text-[11px] font-bold text-slate" data-id="element-326" data-mp="3t50zn9s7k7yb">
                    <TimerIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-327" data-mp="1ypwn83n0x4c" />
                    {s.min}′
                  </span>
                </li>)}
            </ul>
            <p className="mt-3 text-[13px] text-slate" data-id="element-328" data-mp="6vq33k4eg1vz">
              <span className="font-semibold text-ink" data-id="element-329" data-mp="bpkt15alld3i">Game (7 min):</span> Cartwheel the river — wheel across without “falling in.”{' '}
              <span className="font-semibold text-ink" data-id="element-330" data-mp="3acfbzcrgfnot">Cool-down (5 min):</span> side stretch, star shape, one win each.
            </p>
          </PageFrame>

          <PageFrame tag="Page B · Skill Spotlight" title="Week 19 Skill: Cartwheel Hands-Feet Rhythm" data-id="element-331" data-mp="1whgo95qfroi6">
            <p className="mt-2 text-[13px] text-slate" data-id="element-332" data-mp="1shy5afi6dnhz">
              The cartwheel is a wheel: four even contacts in a row — hand, hand, foot, foot — passing sideways over a line.
            </p>
            <p className="mt-4 text-[11px] font-bold uppercase tracking-widest text-coral-deep" data-id="element-333" data-mp="1ihnrasobgc1r">Progression Ladder</p>
            <ol className="mt-2 grid gap-2" data-id="element-334" data-mp="1dl70fqpqfjkz">
              {ladder.map(l => <li key={l.level} className="rounded-xl bg-cloud p-3" data-id="element-335" data-mp="18runiduf1182">
                  <div className="flex items-center gap-2" data-id="element-336" data-mp="14qsony4eur6m">
                    <Dots n={l.dots} data-id="element-337" data-mp="14p7ui9m0da9a" />
                    <span className="text-[12px] font-bold uppercase tracking-wider text-ink" data-id="element-338" data-mp="zsr6dfet6vw7">{l.level}</span>
                  </div>
                  <p className="mt-1 text-[12.5px] leading-snug text-slate" data-id="element-339" data-mp="urtgegnk4xk5">{l.body}</p>
                </li>)}
            </ol>
            <div className="mt-4 grid gap-2 text-[12.5px]" data-id="element-340" data-mp="pwe59wgxpfrf">
              <p className="flex gap-2 text-ink" data-id="element-341" data-mp="g4uokkksg14w">
                <LightbulbIcon className="mt-0.5 h-4 w-4 flex-none text-amber-deep" aria-hidden="true" data-id="element-342" data-mp="l58zcadrxp6m" />
                <span data-id="element-343" data-mp="760zdf9sed1d"><span className="font-semibold" data-id="element-344" data-mp="7698cw2sgy7a">Top cue:</span> “Hand, hand, foot, foot — like a wheel on the line.”</span>
              </p>
              <p className="flex gap-2 text-ink" data-id="element-345" data-mp="b7usn2hnkeri">
                <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full bg-coral text-[9px] font-bold text-white" data-id="element-346" data-mp="yx7305opusve">!</span>
                <span data-id="element-347" data-mp="13v8ikgmxoa24"><span className="font-semibold" data-id="element-348" data-mp="13v8mgrq20u7e">#1 error → fix:</span> feet land together. Put four spots on the line: “put each one down in order.”</span>
              </p>
              <p className="flex gap-2 text-ink" data-id="element-349" data-mp="u4w1wzi8p119">
                <HandIcon className="mt-0.5 h-4 w-4 flex-none text-teal-deep" aria-hidden="true" data-id="element-350" data-mp="1hvt2jg4l4bx7" />
                <span data-id="element-351" data-mp="1mmhsgr0m90u8"><span className="font-semibold" data-id="element-352" data-mp="1mmhwd23qlkzi">Spotting &amp; Safety:</span> stand behind, support the lower back as the hips pass over; clear mat line.</span>
              </p>
            </div>
          </PageFrame>
        </div>

        <p className="mt-6 text-center text-[13px] text-slate" data-id="element-353" data-mp="32eln0jrln6do">
          Volume 3 of 6 · 9 weekly classes + 5 worksheets + Coach&apos;s Notes and Quick Wins in every volume
        </p>
      </div>
    </section>;
}